<template>
  <main>
    <div class="release-bundle-header">
      <div class="text-h5">Software releases</div>
      <v-btn color="primary" @click="openCreateNewReleaseDialog"
        >Create new</v-btn
      >
      <v-btn color="secondary" @click="showCompareDialog = true">Compare</v-btn>
      <v-text-field
        class="search-field"
        label="Search"
        append-icon="mdi-magnify"
        single-line
        hide-details
      />
    </div>
    <!-- RELEASE BUNDLE LIST -->
    <v-data-table
      item-key="id"
      :headers="releaseBundleHeaders"
      :items="releaseBundlesMinimal"
      :expanded.sync="expandedReleaseBundle"
      hide-default-footer
      :loading="$apollo.queries.releaseBundlesMinimal.loading"
      disable-pagination
      show-expand
      single-expand
    >
      <!-- Release channels column -->
      <template v-slot:item.releaseChannels="{ item }">
        <template v-for="channel in item.releaseChannels">
          <v-chip :key="channel.value" small class="mr-1" color="primary">{{
            channelToText(channel)
          }}</v-chip>
        </template>
      </template>

      <template v-slot:item.isNewChanges="{ item }">
        <v-tooltip
          top
          v-if="item.info && item.info.changesSinceLatestRelease.length > 0"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" color="warning">
              mdi-alert-circle-outline
            </v-icon>
          </template>
          <span>
            New changes not released:
            {{ item.info.changesSinceLatestRelease.join(', ') }}
          </span>
        </v-tooltip>
      </template>

      <!-- RELEASE BUNDLE ACTIONS -->
      <template v-slot:item.actions="{ item }">
        <div class="d-flex flex-row justify-space-end align-center">
          <v-spacer />
          <!-- Change channels -->
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-on="on"
                v-bind="attrs"
                @click="openChangeChannelsDialog(item.id, item.releaseChannels)"
              >
                <v-icon>mdi-multicast</v-icon>
              </v-btn>
            </template>
            <span>Change release channels</span>
          </v-tooltip>

          <!-- Delete release bundle -->
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-on="on"
                v-bind="attrs"
                icon
                @click="deleteReleaseBundle(item.id, item.version)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Delete release bundle</span>
          </v-tooltip>
        </div>
      </template>

      <!-- EXPANDED RELEASE BUNDLE -->
      <template v-slot:expanded-item="{ headers, item: releaseBundle }">
        <td :colspan="headers.length" class="py-5 pa-3 ma-0">
          <div class="d-flex flex-row justify-space-between align-center">
            <div>
              <v-btn
                @click="openGenerateReleaseDialog(releaseBundle.id)"
                color="primary"
                class="mr-5"
                >Generate release</v-btn
              >
              <v-btn
                color="primary"
                class="mr-5"
                @click="openEditReleaseBundle(releaseBundle.id)"
                >Edit</v-btn
              >
            </div>
            <v-btn
              color="secondary"
              @click="deleteAllInternalReleaseBundleBuilds"
              >Delete all internal</v-btn
            >
          </div>

          <!-- GENERATED RELEASE BUNDLE RELEASES -->
          <v-data-table
            class="mt-4"
            item-key="id"
            :headers="releaseBundleBuildHeaders"
            :items="releaseBundleBuildsMinimal"
            :loading="$apollo.queries.releaseBundleBuildsMinimal.loading"
            show-expand
            single-expand
            hide-default-footer
            disable-pagination
          >
            <!-- Publication status column-->
            <template v-slot:item.publicationStatus="{ item }">
              <span>{{ publicationStatusToText(item.publicationStatus) }}</span>
            </template>

            <template v-slot:item.updatedAt="{ item }">
              {{ formatDate(item.updatedAt) }}
            </template>

            <!-- RELEASE BUNDLE RELEASE ACTIONS -->
            <template v-slot:item.actions="{ item }">
              <div class="d-flex flex-row justify-space-end align-center">
                <v-spacer />
                <!-- Download release -->

                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      icon
                      @click="initiateReleaseDownload(item.id)"
                      :loading="downloadingReleaseBundleBuildId === item.id"
                    >
                      <v-icon>mdi-download</v-icon>
                    </v-btn>
                  </template>
                  <span>Download</span>
                </v-tooltip>

                <!-- Copy to clipboard -->
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      icon
                      @click="copyReleaseBundleBuildToClipboard(item)"
                    >
                      <v-icon
                        v-if="copiedToClipboardId === item.id"
                        color="green"
                        >mdi-check</v-icon
                      >
                      <v-icon v-else>mdi-clipboard-text</v-icon>
                    </v-btn>
                  </template>
                  <span>Copy info to clipboard</span>
                </v-tooltip>

                <!-- Change publication status -->
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      icon
                      @click="
                        openChangePublicationStatusDialog(
                          item.id,
                          item.publicationStatus,
                        )
                      "
                    >
                      <v-icon>mdi-earth</v-icon>
                    </v-btn>
                  </template>
                  <span>Change publication status</span>
                </v-tooltip>

                <!-- Delete build -->
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      icon
                      @click="deleteReleaseBundleBuild(item.id, item.version)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete build</span>
                </v-tooltip>
              </div>
            </template>

            <template
              v-slot:expanded-item="{ headers, item: releaseBundleBuild }"
            >
              <td :colspan="headers.length" class="py-5 pa-3 ma-0">
                <release-bundle-editor
                  :readonly="true"
                  :releaseBundleBuildId="releaseBundleBuild.id"
                />
              </td>
            </template>
          </v-data-table>
        </td>
      </template>
    </v-data-table>

    <!-- COMPARE RELEASE DIALOG -->
    <v-dialog v-model="showCompareDialog" max-width="1000px">
      <v-card class="pa-6">
        <release-bundle-comparator />
      </v-card>
    </v-dialog>

    <!-- CREATE NEW RELEASE DIALOG -->
    <v-dialog v-model="createReleaseData.show" max-width="600px" persistent>
      <v-card>
        <v-card-title>Create new release</v-card-title>
        <v-card-text>
          <v-form ref="createReleaseForm" v-model="createReleaseData.valid">
            <v-text-field
              v-model="createReleaseData.version"
              label="Version (major.minor)"
              :rules="[rules.required, rules.versionMajorMinor]"
            />
            <v-text-field v-model="createReleaseData.name" label="Name" />
            <v-combobox
              v-model="createReleaseData.channels"
              :items="CHANNELS"
              :rules="[rules.required]"
              label="Channels"
              item-text="name"
              item-value="value"
              multiple
              chips
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn color="secondary" @click="createReleaseData.show = false">
            Close
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="createReleaseData.isLoading"
            @click="createNewReleaseBundle"
            >Create</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CHANGE RELEASE CHANNELS DIALOG -->
    <v-dialog v-model="changeChannelsData.show" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span>Change release channels</span>
        </v-card-title>
        <v-card-text>
          <v-form
            ref="changeReleaseChannelsForm"
            v-model="changeChannelsData.valid"
          >
            <v-combobox
              v-model="changeChannelsData.releaseChannels"
              :items="CHANNELS"
              :rules="[rules.required]"
              label="Release Channels"
              item-text="name"
              item-value="value"
              multiple
              chips
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn color="secondary" @click="changeChannelsData.show = false">
            Close
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="changeChannelsData.isLoading"
            @click="updateReleaseChannels"
            >Change</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CHANGE PUBLICATION STATUS DIALOG -->
    <v-dialog v-model="changePublicationStatusData.show" max-width="600px">
      <v-card>
        <v-card-title>
          <span>Change publication status</span>
        </v-card-title>
        <v-card-text>
          <v-form
            ref="changePublicationStatusForm"
            v-model="changePublicationStatusData.valid"
          >
            <v-select
              v-model="changePublicationStatusData.publicationStatus"
              :items="PUBLICATION_STATUSES"
              :rules="[rules.required]"
              label="Publication Status"
              item-text="name"
              item-value="value"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="secondary"
            @click="changePublicationStatusData.show = false"
          >
            Close
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="changePublicationStatusData.isLoading"
            @click="updatePublicationStatus"
            >Change</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- GENERATE RELEASE BUNDLE RELEASE DIALOG -->
    <v-dialog v-model="generateReleaseBundleBuildData.show" max-width="600px">
      <v-card>
        <v-card-title>
          <span>Generate release bundle release</span>
        </v-card-title>
        <v-card-text>
          <v-form
            ref="generateReleaseBundleBuildForm"
            v-model="generateReleaseBundleBuildData.valid"
          >
            <v-text-field
              v-model.number="generateReleaseBundleBuildData.patch"
              :prefix="generateReleaseBundleBuildData.majorMinor + '.'"
              label="Version (patch)"
              type="number"
              :rules="[rules.requiredNumber, rules.patchNumber]"
              min="0"
              step="1"
            />

            <v-select
              v-model="generateReleaseBundleBuildData.publicationStatus"
              :items="PUBLICATION_STATUSES"
              :rules="[rules.required]"
              label="Publication Status"
              item-text="name"
              item-value="value"
              chips
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="generateReleaseBundleBuildData.show = false"
            >Close</v-btn
          >
          <v-btn
            color="primary"
            :loading="generateReleaseBundleBuildData.isLoading"
            @click="generateReleaseBundleBuild"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <release-download-dialog
      ref="releaseDownloadDialog"
      @download-state-change="handleDownloadStateChange"
    />

    <!-- ERROR POPUP -->
    <Popup
      :show="showErrorMessage"
      :text="errorMessage"
      title="Error"
      type="error"
      @close="showErrorMessage = false"
    />
    <!-- CONFIRMATION DIALOG -->
    <confirmation-dialog ref="confirmationDialog" />
  </main>
</template>

<script>
import { CHANNELS, errorToText, channelToText } from '../../utils/releaseUtils';
import {
  GET_RELEASE_BUNDLE_BUILD,
  GET_ALL_RELEASE_BUNDLES_MINIMAL,
  GET_ALL_RELEASE_BUNDLE_BUILDS_MINIMAL,
  GET_NEXT_RELEASE_BUNDLE_BUILD_VERSION,
  GENERATE_RELEASE_BUNDLE_BUILD,
  CREATE_RELEASE_BUNDLE,
  UPDATE_RELEASE_CHANNELS,
  UPDATE_RELEASE_BUNDLE_BUILD_PUBLICATION_STATUS,
  DELETE_RELEASE_BUNDLE,
  DELETE_RELEASE_BUNDLE_BUILD,
  DELETE_ALL_INTERNAL_RELEASE_BUNDLE_BUILDS,
} from '../../graphql/upgradePackageQueries';

const PUBLICATION_STATUSES = [
  { name: 'Internal', value: 'INTERNAL' },
  { name: 'Public', value: 'PUBLIC' },
];

export default {
  components: {
    ReleaseBundleComparator: () => import('./ReleaseBundleComparator.vue'),
    ReleaseBundleEditor: () => import('./ReleaseBundleEditor.vue'),
    ReleaseDownloadDialog: () => import('./ReleaseDownloadDialog.vue'),
    ConfirmationDialog: () => import('../common/CConfirmationDialog'),
    Popup: () => import('../common/Popup'),
  },
  data() {
    return {
      CHANNELS,
      PUBLICATION_STATUSES,

      showCompareDialog: false,
      copiedToClipboardId: null,
      expandedReleaseBundle: [],
      releaseBundlesMinimal: [], // List of all release bundles (minimal info)
      releaseBundleBuildsMinimal: [], // List of all builds for the expanded release bundle (minimal info)

      downloadingReleaseBundleBuildId: null,

      releaseBundleHeaders: [
        { text: 'Version', value: 'version', align: 'left', sortable: false },
        { text: 'Name', value: 'name', align: 'left', sortable: false },
        { text: 'Channels', value: 'releaseChannels', sortable: false },
        { text: '', value: 'isNewChanges', sortable: false },
        {
          text: '',
          value: 'actions',
          sortable: false,
        },
      ],
      releaseBundleBuildHeaders: [
        { text: 'Version', value: 'version', align: 'left', sortable: false },
        {
          text: 'Publication',
          value: 'publicationStatus',
          align: 'left',
          sortable: false,
        },
        { text: 'Build', value: 'buildNumber', sortable: false },
        { text: 'Updated at', value: 'updatedAt', sortable: false },
        {
          text: '',
          value: 'actions',
          sortable: false,
        },
      ],

      // Form/dialog data
      createReleaseData: {
        show: false,
        valid: false,
        isLoading: false,
        name: '',
        version: '',
        channels: [],
      },
      changeChannelsData: {
        show: false,
        valid: false,
        isLoading: false,
        releaseBundleId: null,
        releaseChannels: [],
      },
      generateReleaseBundleBuildData: {
        show: false,
        valid: false,
        isLoading: false,
        releaseBundleId: null,
        majorMinor: '',
        patch: null,
        publicationStatus: null,
      },
      changePublicationStatusData: {
        show: false,
        valid: false,
        isLoading: false,
        publicationStatus: null,
        releaseBundleBuildId: null,
      },
      rules: {
        versionMajorMinor: (v) =>
          /^\d+\.\d+$/.test(String(v).trim()) ||
          'Invalid version format (major.minor)',
        patchNumber: (v) =>
          /^\d+$/.test(String(v)) || 'Patch must be a non-negative integer',
        required: (v) => (!!v && v.length > 0) || 'Required',
        requiredNumber: (v) => ((!!v || v === 0) && v >= 0) || 'Required',
      },

      showErrorMessage: false,
      errorMessage: '',
    };
  },
  apollo: {
    $client: 'upgradePackageClient',
    releaseBundlesMinimal: {
      query: GET_ALL_RELEASE_BUNDLES_MINIMAL,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          includeInfo: true,
        };
      },
      update: (data) => data.getAllReleaseBundles,
    },
    releaseBundleBuildsMinimal: {
      query: GET_ALL_RELEASE_BUNDLE_BUILDS_MINIMAL,
      fetchPolicy: 'no-cache',
      update: (data) => data.getAllReleaseBundleBuilds,
      variables() {
        return {
          releaseBundleId: this.expandedReleaseBundle[0].id,
        };
      },
      skip() {
        return this.expandedReleaseBundle.length === 0;
      },
    },
  },
  methods: {
    channelToText,
    openCreateNewReleaseDialog() {
      if (this.$refs.createReleaseForm)
        this.$refs.createReleaseForm.resetValidation();

      this.createReleaseData.version = '';
      this.createReleaseData.name = '';
      this.createReleaseData.channels = [];
      this.createReleaseData.isLoading = false;
      this.createReleaseData.valid = true;
      this.createReleaseData.show = true;
    },
    openChangeChannelsDialog(releaseBundleId, releaseChannels) {
      if (this.$refs.changeReleaseChannelsForm)
        this.$refs.changeReleaseChannelsForm.resetValidation();

      this.changeChannelsData.releaseChannels = releaseChannels.map((c) => {
        const channel = CHANNELS.find((ch) => ch.value === c);
        if (channel) return channel;
        return { name: c, value: c };
      });

      this.changeChannelsData.releaseBundleId = releaseBundleId;
      this.changeChannelsData.isLoading = false;
      this.changeChannelsData.valid = true;
      this.changeChannelsData.show = true;
    },
    async openGenerateReleaseDialog(releaseBundleId) {
      if (this.$refs.generateReleaseBundleBuildForm)
        this.$refs.generateReleaseBundleBuildForm.resetValidation();

      const res = await this.$apollo.query({
        query: GET_NEXT_RELEASE_BUNDLE_BUILD_VERSION,
        variables: {
          releaseBundleId: releaseBundleId,
        },
        fetchPolicy: 'no-cache',
      });

      const version = res.data.getNextReleaseBundleBuildVersion || '';

      const m = String(version)
        .trim()
        .match(/^(\d+)\.(\d+)\.(\d+)$/);
      const major = m ? m[1] : '0';
      const minor = m ? m[2] : '0';
      const patch = m ? Number(m[3]) : 0;

      this.generateReleaseBundleBuildData.releaseBundleId = releaseBundleId;
      this.generateReleaseBundleBuildData.majorMinor = `${major}.${minor}`;
      this.generateReleaseBundleBuildData.patch = patch;
      this.generateReleaseBundleBuildData.publicationStatus = null;
      this.generateReleaseBundleBuildData.isLoading = false;
      this.generateReleaseBundleBuildData.valid = true;
      this.generateReleaseBundleBuildData.show = true;
    },
    async openChangePublicationStatusDialog(
      releaseBundleBuildId,
      currentPublicationStatus,
    ) {
      this.changePublicationStatusData.releaseBundleBuildId =
        releaseBundleBuildId;
      this.changePublicationStatusData.publicationStatus =
        currentPublicationStatus;
      this.changePublicationStatusData.isLoading = false;
      this.changePublicationStatusData.valid = true;
      this.changePublicationStatusData.show = true;
    },
    openEditReleaseBundle(id) {
      this.$router.push({ path: `/releasebundleeditor/${id}` });
    },
    async createNewReleaseBundle() {
      try {
        if (!this.$refs.createReleaseForm.validate()) return;

        this.createReleaseData.isLoading = true;

        const { version, name, channels } = this.createReleaseData;

        const response = await this.$apollo.mutate({
          mutation: CREATE_RELEASE_BUNDLE,
          variables: {
            version: version,
            name: name,
            releaseChannels: channels.map((c) => c.value),
          },
        });
        const res = response.data.createReleaseBundle;

        if (res.result === 'OK') {
          this.createReleaseData.show = false;
          this.releaseBundlesMinimal.unshift(res.releaseBundle);
          return;
        } else {
          this.displayError(errorToText(res));
        }
      } catch (err) {
        console.error(err);
        this.displayError('Failed to create release bundle');
      } finally {
        this.createReleaseData.isLoading = false;
      }
    },
    async generateReleaseBundleBuild() {
      try {
        if (!this.$refs.generateReleaseBundleBuildForm.validate()) return;

        this.generateReleaseBundleBuildData.isLoading = true;

        const { majorMinor, patch, releaseBundleId, publicationStatus } =
          this.generateReleaseBundleBuildData;

        const version = `${majorMinor}.${patch}`;

        const response = await this.$apollo.mutate({
          mutation: GENERATE_RELEASE_BUNDLE_BUILD,
          variables: {
            releaseBundleId,
            version,
            publicationStatus,
          },
        });

        const res = response.data.generateReleaseBundleBuild;

        if (res.result !== 'OK') {
          this.displayError(errorToText(res));
          return;
        }

        this.generateReleaseBundleBuildData.show = false;
        await this.$apollo.queries.releaseBundlesMinimal.refetch();
        await this.$apollo.queries.releaseBundleBuildsMinimal.refetch();
      } catch (err) {
        console.error(err);
        this.displayError('Failed to generate release bundle release');
      } finally {
        this.generateReleaseBundleBuildData.isLoading = false;
      }
    },
    async copyReleaseBundleBuildToClipboard(releaseBundleBuild) {
      try {
        if (!releaseBundleBuild?.id) return;

        const res = await this.$apollo.query({
          query: GET_RELEASE_BUNDLE_BUILD,
          fetchPolicy: 'no-cache',
          variables: {
            releaseBundleBuildId: releaseBundleBuild.id,
            includeInfo: false,
          },
        });
        const rbr = res.data.getReleaseBundleBuild;
        const rb = rbr.releaseBundle;

        const summaryRows = [
          ['Field', 'Value'],
          ['Release', rb?.name ?? '—'],
          ['Version', `${rbr.version} (Build ${rbr.buildNumber})`],
          ['Status', rbr.publicationStatus ?? '—'],
          [
            'Channels',
            rb?.releaseChannels?.length ? rb.releaseChannels.join(', ') : '—',
          ],
          ['Updated at', rbr.updatedAt ? this.formatDate(rbr.updatedAt) : '—'],
        ];

        const pkgRows = [
          ['Package', 'Version'],
          ...(Array.isArray(rb?.packages)
            ? rb.packages
                .filter((p) => p && p.active)
                .map((p) => [p.name || '—', p.version || '—'])
            : []),
        ];

        // HTML for rich paste
        const esc = (s) =>
          String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');

        const table = (rows) =>
          `<table><tbody>${rows
            .map((row, i) => {
              const tag = i === 0 ? 'th' : 'td';
              return `<tr>${row
                .map((c) => `<${tag}>${esc(c)}</${tag}>`)
                .join('')}</tr>`;
            })
            .join('')}</tbody></table>`;

        const html = `<div>${table(summaryRows)}<p></p>${table(pkgRows)}</div>`;

        // Plain text fallback if pasted location does not support rich text
        const lines = [];
        lines.push(
          `Release: ${rb?.name ?? '—'} ${rbr.version} (Build ${
            rbr.buildNumber
          })`,
        );
        lines.push(`Status: ${rbr.publicationStatus ?? '—'}`);
        if (rb?.releaseChannels?.length)
          lines.push(`Channels: ${rb.releaseChannels.join(', ')}`);
        if (rbr.updatedAt)
          lines.push(`Updated at: ${this.formatDate(rbr.updatedAt)}`);

        const activePkgs = Array.isArray(rb?.packages)
          ? rb.packages.filter((p) => p && p.active)
          : [];

        if (activePkgs.length) {
          lines.push('');
          lines.push('Packages:');
          for (const p of activePkgs) {
            lines.push(`- ${p.name || '—'} (${p.version || '—'})`);
          }
        }
        const plain = lines.join('\n');

        try {
          if (navigator.clipboard?.write && window.ClipboardItem) {
            const item = new window.ClipboardItem({
              'text/html': new Blob([html], { type: 'text/html' }),
              'text/plain': new Blob([plain], { type: 'text/plain' }),
            });
            await navigator.clipboard.write([item]);
          } else {
            await navigator.clipboard.writeText(plain);
          }
        } catch {
          await navigator.clipboard.writeText(plain);
        }

        this.copiedToClipboardId = rb.id;
        setTimeout(() => {
          if (this.copiedToClipboardId === rb.id)
            this.copiedToClipboardId = null;
        }, 2000);
      } catch (err) {
        console.error(err);
        this.displayError('Failed to copy release bundle release to clipboard');
      }
    },
    async deleteReleaseBundle(id, version) {
      try {
        if (
          !(await this.$refs.confirmationDialog.open(
            `Are you sure you want to delete release ${version ?? 'unknown'}?`,
          ))
        )
          return;

        const response = await this.$apollo.mutate({
          mutation: DELETE_RELEASE_BUNDLE,
          variables: {
            releaseBundleId: id,
          },
        });

        const res = response.data.deleteReleaseBundle;

        if (res.result !== 'OK') {
          this.displayError(errorToText(res));
          return;
        }

        const index = this.releaseBundlesMinimal.findIndex((b) => b.id === id);
        if (index !== -1) this.releaseBundlesMinimal.splice(index, 1);
      } catch (err) {
        console.error(err);
        this.displayError('Failed to delete release bundle');
      }
    },
    async deleteReleaseBundleBuild(id, version) {
      try {
        if (
          !(await this.$refs.confirmationDialog.open(
            `Are you sure you want to delete release bundle release ${version}?`,
          ))
        )
          return;

        const response = await this.$apollo.mutate({
          mutation: DELETE_RELEASE_BUNDLE_BUILD,
          variables: {
            releaseBundleBuildId: id,
          },
        });

        const res = response.data.deleteReleaseBundleBuild;

        if (res.result !== 'OK') {
          this.displayError(errorToText(res));
          return;
        }

        const index = this.releaseBundleBuildsMinimal.findIndex(
          (b) => b.id === id,
        );
        if (index !== -1) this.releaseBundleBuildsMinimal.splice(index, 1);

        await this.$apollo.queries.releaseBundlesMinimal.refetch(); // Refetch new version of working copy if changed by build deletion
      } catch (err) {
        console.error(err);
        this.displayError('Failed to delete release bundle release');
      }
    },
    async deleteAllInternalReleaseBundleBuilds() {
      try {
        if (
          !(await this.$refs.confirmationDialog.open(
            `Are you sure you want to delete all internal release bundle releases?`,
          ))
        )
          return;

        const response = await this.$apollo.mutate({
          mutation: DELETE_ALL_INTERNAL_RELEASE_BUNDLE_BUILDS,
          variables: {
            releaseBundleId: this.expandedReleaseBundle[0].id,
          },
        });

        if (response.data.deleteAllInternalReleaseBundleBuilds) {
          await this.$apollo.queries.releaseBundleBuildsMinimal.refetch();
        } else {
          this.displayError(
            'Failed to delete all internal release bundle releases',
          );
        }
      } catch (err) {
        console.error(err);
        this.displayError(
          'Failed to delete all internal release bundle releases',
        );
      }
    },
    async updatePublicationStatus() {
      try {
        if (!this.$refs.changePublicationStatusForm.validate()) return;

        this.changePublicationStatusData.isLoading = true;

        const { releaseBundleBuildId, publicationStatus } =
          this.changePublicationStatusData;

        const response = await this.$apollo.mutate({
          mutation: UPDATE_RELEASE_BUNDLE_BUILD_PUBLICATION_STATUS,
          variables: {
            releaseBundleBuildId: releaseBundleBuildId,
            publicationStatus: publicationStatus,
          },
        });

        const res = response.data.updateReleaseBundleBuildPublicationStatus;

        if (res.result !== 'OK') {
          this.displayError(errorToText(res));
          return;
        }

        this.changePublicationStatusData.show = false;
        this.releaseBundleBuildsMinimal = this.replaceById(
          this.releaseBundleBuildsMinimal,
          releaseBundleBuildId,
          res.releaseBundleBuild,
        );
      } catch (err) {
        console.error(err);
        this.displayError('Failed to update publication status');
      } finally {
        this.changePublicationStatusData.isLoading = false;
      }
    },
    async updateReleaseChannels() {
      try {
        if (!this.$refs.changeReleaseChannelsForm.validate()) return;

        this.changeChannelsData.isLoading = true;
        const { releaseBundleId, releaseChannels } = this.changeChannelsData;

        const response = await this.$apollo.mutate({
          mutation: UPDATE_RELEASE_CHANNELS,
          variables: {
            releaseBundleId: releaseBundleId,
            releaseChannels: releaseChannels.map((c) => c.value),
          },
        });
        const res = response.data.updateReleaseChannels;

        if (res.result !== 'OK') {
          this.displayError(errorToText(res));
          return;
        }

        this.releaseBundlesMinimal = this.replaceById(
          this.releaseBundlesMinimal,
          releaseBundleId,
          res.releaseBundle,
        );
        this.changeChannelsData.show = false;
      } catch (err) {
        console.error(err);
        this.displayError('Failed to update release channels');
      } finally {
        this.changeChannelsData.isLoading = false;
      }
    },
    replaceById(array, id, newItem) {
      return array.map((item) => (item.id === id ? newItem : item));
    },
    formatDate(date) {
      return new Date(date).toLocaleString('no-NO');
    },
    displayError(message) {
      this.errorMessage = message;
      this.showErrorMessage = true;
    },
    publicationStatusToText(publicationStatus) {
      const status = PUBLICATION_STATUSES.find(
        (st) => st.value === publicationStatus,
      );
      if (status) return status.name;
      return publicationStatus;
    },
    handleDownloadStateChange(releaseBundleBuildId) {
      this.downloadingReleaseBundleBuildId = releaseBundleBuildId;
    },
    async initiateReleaseDownload(releaseBundleBuildId) {
      await this.$refs.releaseDownloadDialog.initiateReleaseDownload(
        releaseBundleBuildId,
      );
    },
  },
};
</script>

<style scoped>
.release-bundle-header {
  padding: 12px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.search-field {
  max-width: 250px;
  margin-bottom: 20px;
}

</style>
